import { VercelImage } from "@wags/ui/vercel-image.tsx";
import { ProviderType } from "@wags/catalog/pure.ts";
import { ProviderPhotoStrip } from "../provider-photo-strip";

type MediaItem = { id: string; url: string; mimeType?: string | null };

export function MeetTheProviderSection({
    breederName,
    meetBreederImageUrl,
    breederDescription,
    certifications,
    providerType = ProviderType.Breeder,
    mediaItems,
    onPhotoClick,
}: {
    breederName: string;
    meetBreederImageUrl: string | undefined;
    breederDescription: string;
    certifications: string[];
    providerType?: ProviderType;

    mediaItems?: MediaItem[];
    onPhotoClick?: (index: number) => void;
}) {
    const gallery =
        mediaItems?.length && onPhotoClick
            ? { mediaItems, onPhotoClick }
            : null;

    if (!meetBreederImageUrl && !breederDescription && !gallery) return null;

    const heading =
        providerType === ProviderType.Breeder
            ? "Meet the breeder"
            : "Meet the shelter";

    const bioAndCerts = (
        <div className="flex min-w-0 flex-col">
            {breederDescription && (
                <p className="max-w-prose whitespace-pre-line break-words text-body text-doberman-black/80">
                    {breederDescription}
                </p>
            )}
            {certifications.length > 0 && (
                <div className="mt-6 flex flex-col gap-3">
                    <h3 className="text-body font-semibold text-doberman-black text-pretty">
                        Certifications
                    </h3>
                    {certifications.map((c) => (
                        <p
                            key={c}
                            className="border-l-2 border-labrador-yellow pl-3 text-body-small text-doberman-black/70"
                        >
                            {c}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <section className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-x-16">
            <h2 className="text-section-title text-doberman-black text-balance">
                {heading}
            </h2>

            {gallery ? (
                <div className="mt-5 lg:mt-0 lg:max-w-190">
                    {bioAndCerts}
                    <ProviderPhotoStrip
                        mediaItems={gallery.mediaItems}
                        alt={breederName}
                        onPhotoClick={gallery.onPhotoClick}
                        layout="grid"
                        className="mt-6 sm:grid-cols-3"
                    />
                </div>
            ) : (
                <div className="mt-5 flex flex-col gap-6 md:flex-row md:gap-8 lg:mt-0">
                    {meetBreederImageUrl && (
                        <div className="relative aspect-4/3 w-full shrink-0 overflow-hidden rounded-card img-inset md:w-80">
                            <VercelImage
                                src={meetBreederImageUrl}
                                alt={`${breederName} image`}
                                width={1200}
                                height={900}
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                        </div>
                    )}
                    {bioAndCerts}
                </div>
            )}
        </section>
    );
}
